import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DomainRoles {
    isAdmin: boolean;
    isReadonly: boolean;
    roleByApp: { [app: string]: string };
}

export interface UserState {
    userData: any;
    cp: DomainRoles;
    tbw: DomainRoles;
    /**
     * TBW-specific supported locales sourced from the IAS user entity
     * (`attributes.locales` at /v1/domains/tbw/entity-types/user/entity-ids/me).
     * Drives the Tutorial Language dropdown in TBWDashboardContent. Empty array
     * means "no filter applied" (defensive fallback when the entity has no locales).
     */
    tbwSupportedLocales: string[];
    /**
     * CP-specific supported locales sourced from the LCS user entity
     * (`attributes.locales` at /v1/domains/lcp/entity-types/user/entity-ids/me).
     * Used to disable target-locale checkboxes in CP localization-target dialogs
     * (RequestLocalization, RefreshUtut, etc.). Empty array means "no filter applied".
     */
    cpSupportedLocales: string[];
}

const emptyDomainRoles = (): DomainRoles => ({
    isAdmin: false,
    isReadonly: false,
    roleByApp: {},
});

const initialState: UserState = {
    userData: {},
    cp: emptyDomainRoles(),
    tbw: emptyDomainRoles(),
    tbwSupportedLocales: [],
    cpSupportedLocales: [],
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        updateUserData(state, action: PayloadAction<any>) {
            // Only update if different
            if (state.userData?.['email'] != action.payload?.['email']) {
                state.userData = action.payload;
            }
        },
        setCpRoles(state, action: PayloadAction<DomainRoles>) {
            state.cp = action.payload;
        },
        setTbwRoles(state, action: PayloadAction<DomainRoles>) {
            state.tbw = action.payload;
        },
        updateTbwSupportedLocales(state, action: PayloadAction<string[]>) {
            state.tbwSupportedLocales = action.payload;
        },
        updateCpSupportedLocales(state, action: PayloadAction<string[]>) {
            state.cpSupportedLocales = action.payload;
        },
        updateClearUserData(state, _action: PayloadAction<boolean>) {
            state.userData = {};
            state.cp = emptyDomainRoles();
            state.tbw = emptyDomainRoles();
            state.tbwSupportedLocales = [];
            state.cpSupportedLocales = [];
        },
    },
});

export const {
    updateUserData,
    setCpRoles,
    setTbwRoles,
    updateTbwSupportedLocales,
    updateCpSupportedLocales,
    updateClearUserData,
} = userSlice.actions;
export default userSlice.reducer;
